/**
 * SVG アバター生成（旧 Flask UI の makeAvatar 移植・記憶と顔のパリティ）。
 * 雰囲気（vibe）×色（color）×候補シード から決定論的に SVG 文字列を生成する。
 * 生成した SVG 文字列は AgentProfile.icon に保存し、avatarType="svg" で表示する。
 */

export const VIBES: { key: string; label: string }[] = [
  { key: "star", label: "星（道しるべ）" },
  { key: "circle", label: "ロボット（相棒）" },
  { key: "bolt", label: "稲妻（はやい）" },
  { key: "leaf", label: "葉（育つ）" },
]

export const COLORS: { value: string; label: string }[] = [
  { value: "#0582AF", label: "ポラリスブルー" },
  { value: "#E6694A", label: "夕焼け" },
  { value: "#10B981", label: "新緑" },
  { value: "#7C5CBF", label: "夜空" },
]

const GLYPHS: Record<string, string> = {
  star: '<path d="M50 22 L57 42 L78 42 L61 55 L67 76 L50 63 L33 76 L39 55 L22 42 L43 42 Z" fill="#fff" opacity=".92"/>',
  circle:
    '<circle cx="50" cy="44" r="16" fill="#fff" opacity=".92"/><rect x="34" y="64" width="32" height="12" rx="6" fill="#fff" opacity=".7"/>',
  bolt: '<path d="M55 20 L36 54 L48 54 L43 80 L66 44 L53 44 Z" fill="#fff" opacity=".92"/>',
  leaf: '<path d="M50 20 C75 30 75 60 50 80 C25 60 25 30 50 20 Z" fill="#fff" opacity=".88"/><line x1="50" y1="30" x2="50" y2="72" stroke="#0003" stroke-width="3"/>',
}

function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16)
  const f = (v: number) => Math.max(0, Math.min(255, v + amt))
  return (
    "#" +
    (((f(n >> 16) << 16) | (f((n >> 8) & 255) << 8) | f(n & 255)) >>> 0)
      .toString(16)
      .padStart(6, "0")
  )
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

// makeAvatar が生成する図形のみを許可する厳格な allowlist（denylist ではない）。
const _ALLOWED_TAGS = new Set([
  "svg", "defs", "lineargradient", "stop", "rect", "circle", "path", "line", "g", "text",
])
const _ALLOWED_ATTRS = new Set([
  "viewbox", "xmlns", "x", "y", "cx", "cy", "r", "rx", "ry", "width", "height",
  "fill", "stroke", "stroke-width", "opacity", "d", "transform", "offset",
  "stop-color", "font-size", "font-family", "font-weight", "text-anchor", "id",
  "x1", "y1", "x2", "y2",
])

/**
 * 保存済みアイコン SVG を表示前にサニタイズする（厳格 allowlist）。
 *
 * DOMParser で解析し、許可したタグ・属性だけを残して SVG を再構築する。許可外の
 * タグ・属性（on*・href・xlink・style・script・animate・url(...) 等）はすべて捨てる。
 * 解析に失敗、または許可外要素を含む場合は空文字を返す（自テナントでも XSS を作らない）。
 */
export function sanitizeAvatarSvg(svg: string | undefined): string {
  if (!svg || !svg.trimStart().startsWith("<svg")) return ""
  if (typeof DOMParser === "undefined") return "" // SSR 等では描画しない
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(svg, "image/svg+xml")
  } catch {
    return ""
  }
  if (doc.getElementsByTagName("parsererror").length > 0) return ""
  const root = doc.documentElement
  if (!root || root.tagName.toLowerCase() !== "svg") return ""

  const walk = (el: Element): boolean => {
    if (!_ALLOWED_TAGS.has(el.tagName.toLowerCase())) return false
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      // on*・xlink・style・href 等は明示的に拒否
      if (name.startsWith("on") || name.includes("href") || name.includes("xlink")) return false
      if (!_ALLOWED_ATTRS.has(name)) return false
      const val = attr.value
      // 内部参照 url(#id) のみ許可（グラデーション fill 等）。外部 url()・スキーム付きは拒否
      const urlMatch = val.match(/url\s*\(\s*([^)]*)\)/i)
      if (urlMatch && !/^['"]?#[\w-]+['"]?$/.test(urlMatch[1].trim())) return false
      if (/javascript:|expression\s*\(|data:/i.test(val)) return false
    }
    for (const child of Array.from(el.children)) {
      if (!walk(child)) return false
    }
    return true
  }
  if (!walk(root)) return ""
  // 解析済みツリーを再シリアライズ（元文字列をそのまま返さない）
  return new XMLSerializer().serializeToString(root)
}

/** 決定論的な SVG アバター文字列を返す（旧 UI と同じ生成式）。 */
export function makeAvatar(
  vibe: string,
  color: string,
  seed: number,
  initial: string
): string {
  const tilt = [0, -8, 8, 14][seed % 4]
  const light = ["#ffffff33", "#ffffff22", "#ffffff44", "#ffffff1a"][seed % 4]
  const glyph = GLYPHS[vibe] || GLYPHS.star
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="g${seed}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${color}"/><stop offset="1" stop-color="${shade(color, -28)}"/></linearGradient></defs>
    <rect width="100" height="100" rx="20" fill="url(#g${seed})"/>
    <circle cx="${20 + ((seed * 23) % 60)}" cy="${14 + ((seed * 17) % 18)}" r="26" fill="${light}"/>
    <g transform="rotate(${tilt} 50 50)">${glyph}</g>
    <text x="50" y="93" font-size="13" fill="#fff" text-anchor="middle" font-family="sans-serif" font-weight="700" opacity=".9">${escapeText(initial)}</text>
  </svg>`
}
